import React, { useEffect, useState } from 'react';
import { Paper, Typography, FormControlLabel, Switch, Box } from '@mui/material';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Toggle the temporal system notice banner (e.g. legacy member import).
 */
export default function SystemNoticeSettingsCard({ sx = {} }) {
  const { staff } = useAuth();
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const perms = staff?.permissions || [];
  const canManage = perms.includes('all') || perms.includes('members');

  useEffect(() => {
    if (!canManage) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/app-settings');
        if (!cancelled) setNotice(res.data?.data?.system_notice || null);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManage]);

  const handleToggle = async (e) => {
    const checked = e.target.checked;
    setNotice((prev) => (prev ? { ...prev, enabled: checked } : prev));
    setSaving(true);
    try {
      const res = await api.put('/app-settings', { system_notice_enabled: checked });
      setNotice(res.data?.data?.system_notice || null);
    } catch {
      setNotice((prev) => (prev ? { ...prev, enabled: !checked } : prev));
    } finally {
      setSaving(false);
    }
  };

  if (!canManage || !notice || (!notice.title && !notice.message && !notice.id)) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        ...sx,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        System notice banner
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={!!notice.enabled}
            onChange={handleToggle}
            disabled={loading || saving}
            color="primary"
          />
        }
        label="Show the temporary notice to staff"
      />
      <Box sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5, maxWidth: 640 }}>
        {notice.title ? <strong>{notice.title}</strong> : 'Current notice'}
        {notice.message ? ` — ${notice.message}` : null}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Turn this off when the announcement is no longer needed. Staff can also dismiss it for themselves.
      </Typography>
    </Paper>
  );
}
